import { FormSchema } from '@/utils/formSchema';

/**
 * Gallery 12 schema mẫu BV Hưng Việt.
 * Phân tích từ https://hungviet-form-thanhtoan-muahang.netlify.app/
 *
 * Cách dùng:
 * - Admin vào Visual Builder → bấm "Thư viện" → chọn 1 mẫu → Áp dụng
 * - Schema được clone vào builder, admin chỉnh sửa rồi Save
 *
 * LƯU Ý: Mỗi schema cần file .docx tương ứng để render PDF.
 * Tên field trong schema phải KHỚP với placeholder {{ten_field}} trong Word.
 */

export interface GalleryItem {
  id: string;
  name: string;
  description: string;
  category: string;
  formCode?: string;
  schema: FormSchema;
}

export const TEMPLATE_GALLERY: GalleryItem[] = [

  // ============================================================
  // 1. PHIẾU ĐỀ NGHỊ MUA HÀNG / DỊCH VỤ / SỬA CHỮA (DXMH-01)
  // ============================================================
  {
    id: 'dxmh',
    name: 'Phiếu đề nghị mua hàng/dịch vụ/sửa chữa',
    description: 'Đề xuất mua hàng từ các bộ phận. Có 5 checkbox phân loại + table 8 cột (mã hàng/mô tả/SL/đơn giá/...) + 4 chữ ký.',
    category: 'Mua hàng',
    formCode: 'DXMH-01',
    schema: {
      version: 1,
      paperSize: 'A4',
      formCode: 'DXMH-01',
      blocks: [
        { type: 'header', showLogo: true, showCompany: true },
        { type: 'title', text: 'PHIẾU ĐỀ NGHỊ MUA HÀNG / DỊCH VỤ / SỬA CHỮA' },
        { type: 'spacer', kind: 'space', height: 8 },
        {
          type: 'field_row',
          fields: [
            { name: 'ngay_de_nghi', label: 'Ngày đề nghị', input: 'date', autoFill: 'today' },
            { name: 'nguoi_de_nghi', label: 'Người đề nghị', input: 'text', autoFill: 'current_user_name' },
          ],
        },
        {
          type: 'field_row',
          fields: [
            { name: 'dien_thoai', label: 'Điện thoại', input: 'text' },
            { name: 'bo_phan', label: 'Thuộc bộ phận', input: 'text', autoFill: 'current_user_dept' },
          ],
        },
        {
          type: 'field_row',
          fields: [
            { name: 'chi_nhanh', label: 'Chi nhánh', input: 'text' },
            { name: 'ngay_can_hang', label: 'Ngày cần hàng', input: 'date' },
          ],
        },
        { type: 'field', name: 'dia_diem_giao', label: 'Địa điểm giao hàng', input: 'text' },
        { type: 'spacer', kind: 'space', height: 8 },
        {
          type: 'checkbox_row',
          items: [
            { name: 'mua_them', label: 'Mua thêm', exclusiveGroup: 'loai_mua' },
            { name: 'mua_thay_the', label: 'Mua thay thế', exclusiveGroup: 'loai_mua' },
            { name: 'mua_moi', label: 'Mua mới', exclusiveGroup: 'loai_mua' },
            { name: 'sua_chua', label: 'Sửa chữa', exclusiveGroup: 'loai_mua' },
            { name: 'thuc_hien_gap', label: 'Thực hiện gấp' },
          ],
        },
        { type: 'field', name: 'ly_do_mua', label: 'Ghi lý do mua', input: 'textarea' },
        { type: 'field', name: 'ly_do_gap', label: 'Ghi lý do thực hiện gấp', input: 'textarea' },
        {
          type: 'table',
          name: 'items',
          addable: true,
          minRows: 1,
          columns: [
            { name: '@index', label: 'STT', input: 'auto_index', width: 40 },
            { name: 'ma_hang', label: 'Mã hàng', input: 'text', width: 90 },
            { name: 'mo_ta', label: 'Mô tả', input: 'text' },
            { name: 'dvt', label: 'ĐVT', input: 'text', width: 60 },
            { name: 'so_luong', label: 'SL', input: 'number', width: 60 },
            { name: 'don_gia', label: 'Đơn giá', input: 'number', width: 100 },
            { name: 'thanh_tien', label: 'Thành tiền', input: 'number', width: 110, computed: 'so_luong * don_gia' },
            { name: 'ghi_chu', label: 'Ghi chú', input: 'text', width: 100 },
          ],
          totals: [
            { name: 'tong_truoc_thue', label: 'Tổng tiền trước thuế', sumColumn: 'thanh_tien' },
          ],
        },
        {
          type: 'field_row',
          fields: [
            { name: 'thue_pct', label: 'Thuế (%)', input: 'number', suffix: '%' },
            { name: 'tong_sau_thue', label: 'Tổng tiền sau thuế', input: 'number', readonly: true,
              computed: 'tong_truoc_thue * (1 + thue_pct / 100)' },
          ],
        },
        { type: 'field', name: 'ho_so_kem_theo', label: 'Hồ sơ kèm theo', input: 'textarea' },
        {
          type: 'signatures',
          layout: 'row',
          items: [
            { label: 'Người đề nghị', autoFillUser: true },
            { label: 'Phụ trách bộ phận' },
            { label: 'Phụ trách tư vấn' },
            { label: 'Phê duyệt' },
          ],
        },
      ],
    },
  },

  // ============================================================
  // 2. GIẤY ĐỀ NGHỊ THANH TOÁN (DNTT - đã có sẵn trong app)
  // ============================================================
  {
    id: 'dntt',
    name: 'Giấy đề nghị thanh toán',
    description: 'Tờ trình thanh toán cho phòng tài chính - kế toán. Có 2 checkbox (hoàn ứng/thanh toán), bảng động, chứng từ kèm theo, 5 chữ ký.',
    category: 'Tài chính',
    formCode: 'TUTT/2025/04',
    schema: {
      version: 1,
      paperSize: 'A4',
      formCode: 'TUTT/2025/04',
      blocks: [
        { type: 'header', showLogo: true, showCompany: true },
        { type: 'title', text: 'GIẤY ĐỀ NGHỊ THANH TOÁN' },
        {
          type: 'checkbox_row',
          items: [
            { name: 'is_hoan_ung', label: 'Hoàn ứng', exclusiveGroup: 'kind' },
            { name: 'is_thanh_toan', label: 'Thanh toán', exclusiveGroup: 'kind', default: true },
          ],
        },
        { type: 'field', name: 'ngay', label: 'Ngày', input: 'date', autoFill: 'today' },
        { type: 'text', text: 'Kính gửi: Ban Giám đốc Công ty', italic: true },
        {
          type: 'field_row',
          fields: [
            { name: 'ho_ten', label: 'Tên tôi là', input: 'text', autoFill: 'current_user_name' },
            { name: 'bo_phan', label: 'Bộ phận', input: 'text', autoFill: 'current_user_dept' },
          ],
        },
        { type: 'field', name: 'phu_trach_ncc', label: 'Phụ trách nhà cung cấp', input: 'text' },
        { type: 'field', name: 'ly_do', label: 'Lý do', input: 'textarea' },
        { type: 'section_title', text: 'I - Số tiền đề nghị thanh toán:' },
        {
          type: 'table',
          name: 'items',
          addable: true,
          minRows: 1,
          columns: [
            { name: '@index', label: 'TT', input: 'auto_index', width: 40 },
            { name: 'noi_dung', label: 'Nội dung', input: 'text' },
            { name: 'dvt', label: 'ĐVT', input: 'text', width: 70 },
            { name: 'so_luong', label: 'Số lượng', input: 'number', width: 80 },
            { name: 'don_gia', label: 'Đơn giá', input: 'number', width: 110 },
            { name: 'thue', label: 'Thuế (%)', input: 'number', width: 80 },
            { name: 'thanh_tien', label: 'Thành tiền', input: 'number', width: 130,
              computed: 'so_luong * don_gia * (1 + thue / 100)' },
          ],
          totals: [{ name: 'tong_cong', label: 'Cộng', sumColumn: 'thanh_tien' }],
        },
        { type: 'section_title', text: 'II - Số tiền đã tạm ứng:' },
        { type: 'field', name: 'so_tam_ung', label: 'Số tạm ứng', input: 'number', suffix: 'đồng', indent: true },
        { type: 'section_title', text: 'III - Chênh lệch:' },
        { type: 'field', name: 'chenh_lech_chi_khong_het', label: '1. Số tạm ứng chi không hết (II - I)',
          input: 'number', suffix: 'đồng', readonly: true, indent: true,
          computed: 'Math.max(0, so_tam_ung - tong_cong)' },
        { type: 'field', name: 'so_con_phai_tt', label: '2. Số còn phải thanh toán (I - II)',
          input: 'number', suffix: 'đồng', readonly: true, indent: true,
          computed: 'Math.max(0, tong_cong - so_tam_ung)' },
        { type: 'field', name: 'bang_chu', label: 'Bằng chữ', input: 'text', readonly: true,
          computedWords: 'so_con_phai_tt' },
        { type: 'text', text: 'Tôi xin chịu trách nhiệm về nội dung thanh toán trên.', italic: true, align: 'center' },
        { type: 'section_title', text: 'Chứng từ kèm theo hồ sơ này:' },
        {
          type: 'checklist',
          label: '',
          columns: 1,
          items: [
            { name: 'ct_dxmh', label: 'Đề xuất mua hàng số', withInput: true },
            { name: 'ct_hopdong', label: 'Hợp đồng/Đơn hàng (PO) số', withInput: true },
            { name: 'ct_dntt_ncc', label: 'Giấy đề nghị thanh toán của nhà cung cấp' },
            { name: 'ct_yeu_cau_mua', label: 'Phiếu yêu cầu mua vật tư' },
            { name: 'ct_phieu_nhap', label: 'Phiếu nhập kho/ Biên bản giao nhận - nghiệm thu' },
            { name: 'ct_kt_chat_luong', label: 'Phiếu kiểm tra chất lượng' },
            { name: 'ct_hoa_don', label: 'Hóa đơn tài chính hợp lệ' },
          ],
        },
        {
          type: 'signatures',
          layout: 'row',
          items: [
            { label: 'Giám đốc điều hành' },
            { label: 'Kế toán trưởng' },
            { label: 'Trưởng khối' },
            { label: 'Phụ trách bộ phận' },
            { label: 'Người đề nghị', autoFillUser: true },
          ],
        },
      ],
    },
  },

  // ============================================================
  // 3. GIẤY ĐỀ NGHỊ TẠM ỨNG NHÀ CUNG CẤP (TUTT/2025/01)
  // ============================================================
  {
    id: 'dntu',
    name: 'Giấy đề nghị tạm ứng nhà cung cấp',
    description: 'Tạm ứng tiền cho NCC. Radio Tiền mặt/Chuyển khoản, có conditional show bank info, theo dõi công nợ tạm ứng, 5 chữ ký.',
    category: 'Tài chính',
    formCode: 'TUTT/2025/01',
    schema: {
      version: 1,
      paperSize: 'A4',
      formCode: 'TUTT/2025/01',
      blocks: [
        { type: 'header', showLogo: true, showCompany: true },
        { type: 'title', text: 'GIẤY ĐỀ NGHỊ TẠM ỨNG CHO NHÀ CUNG CẤP' },
        { type: 'text', text: 'Kính gửi: BAN LÃNH ĐẠO CÔNG TY', bold: true, italic: true, align: 'center' },
        {
          type: 'field_row',
          fields: [
            { name: 'ho_ten', label: 'Tên tôi là', input: 'text', autoFill: 'current_user_name' },
            { name: 'bo_phan', label: 'Bộ phận', input: 'text', autoFill: 'current_user_dept' },
          ],
        },
        { type: 'field', name: 'phu_trach_ncc', label: 'Phụ trách nhà cung cấp', input: 'text' },
        { type: 'field', name: 'so_tien', label: 'Đề nghị cho tôi tạm ứng số tiền cho nhà cung cấp', input: 'number', suffix: 'đồng' },
        { type: 'field', name: 'bang_chu', label: 'Bằng chữ', input: 'text', readonly: true, computedWords: 'so_tien' },
        {
          type: 'checkbox_row',
          items: [
            { name: 'is_tien_mat', label: 'Tiền mặt', exclusiveGroup: 'payment_method' },
            { name: 'is_chuyen_khoan', label: 'Chuyển khoản', exclusiveGroup: 'payment_method', default: true },
          ],
        },
        // Bank info chỉ hiện khi tick "Chuyển khoản"
        {
          type: 'bank_info',
          beneficiaryName: 'beneficiary',
          accountNumberName: 'account_number',
          bankName: 'bank_name',
          showWhenField: 'is_chuyen_khoan',
          showWhenValue: 'true',
        },
        { type: 'field', name: 'ly_do', label: 'Lý do', input: 'textarea' },
        {
          type: 'field_row',
          fields: [
            { name: 'so_dntt', label: 'Kèm Giấy đề nghị thanh toán số', input: 'text' },
            { name: 'ngay_dntt', label: 'Ngày', input: 'date' },
          ],
        },
        { type: 'field', name: 'thoi_gian_dk', label: 'Thời gian dự kiến thanh toán tạm ứng', input: 'date' },
        { type: 'spacer', kind: 'line', height: 8 },
        { type: 'section_title', text: 'Phần theo dõi công nợ tạm ứng của nhà cung cấp:' },
        {
          type: 'field_row',
          fields: [
            { name: 'tong_chua_hoan', label: 'Tổng tạm ứng chưa hoàn', input: 'text' },
            { name: 'tri_gia_chua_hoan', label: 'Trị giá', input: 'number', suffix: 'đồng' },
          ],
        },
        {
          type: 'field_row',
          fields: [
            { name: 'so_qua_han', label: 'Số khoản tạm ứng quá hạn', input: 'text' },
            { name: 'tri_gia_qua_han', label: 'Trị giá', input: 'number', suffix: 'đồng' },
          ],
        },
        { type: 'field', name: 'ngay_lap', label: 'Ngày', input: 'date', autoFill: 'today' },
        {
          type: 'signatures',
          layout: 'row',
          items: [
            { label: 'Giám đốc điều hành' },
            { label: 'Kế toán trưởng' },
            { label: 'Trưởng khối' },
            { label: 'Phụ trách bộ phận' },
            { label: 'Người đề nghị', autoFillUser: true },
          ],
        },
      ],
    },
  },

  // ============================================================
  // 4. GIẤY ĐỀ NGHỊ TẠM ỨNG CÁ NHÂN
  // ============================================================
  {
    id: 'tucn',
    name: 'Giấy đề nghị tạm ứng cá nhân',
    description: 'Tạm ứng tiền cá nhân (không có "phụ trách NCC" như mẫu DNTU). Tương tự DNTU nhưng dành cho nhân viên cá nhân.',
    category: 'Tài chính',
    formCode: 'TUTT/2025/01',
    schema: {
      version: 1,
      paperSize: 'A4',
      formCode: 'TUTT/2025/01',
      blocks: [
        { type: 'header', showLogo: true, showCompany: true },
        { type: 'title', text: 'GIẤY ĐỀ NGHỊ TẠM ỨNG CÁ NHÂN' },
        { type: 'text', text: 'Kính gửi: BAN LÃNH ĐẠO CÔNG TY', bold: true, italic: true, align: 'center' },
        {
          type: 'field_row',
          fields: [
            { name: 'ho_ten', label: 'Tên tôi là', input: 'text', autoFill: 'current_user_name' },
            { name: 'bo_phan', label: 'Bộ phận', input: 'text', autoFill: 'current_user_dept' },
          ],
        },
        { type: 'field', name: 'so_tien', label: 'Đề nghị cho tôi tạm ứng số tiền', input: 'number', suffix: 'đồng' },
        { type: 'field', name: 'bang_chu', label: 'Bằng chữ', input: 'text', readonly: true, computedWords: 'so_tien' },
        {
          type: 'checkbox_row',
          items: [
            { name: 'is_tien_mat', label: 'Tiền mặt', exclusiveGroup: 'payment_method' },
            { name: 'is_chuyen_khoan', label: 'Chuyển khoản', exclusiveGroup: 'payment_method', default: true },
          ],
        },
        {
          type: 'bank_info',
          beneficiaryName: 'beneficiary',
          accountNumberName: 'account_number',
          bankName: 'bank_name',
          showWhenField: 'is_chuyen_khoan',
          showWhenValue: 'true',
        },
        { type: 'field', name: 'ly_do', label: 'Lý do', input: 'textarea' },
        { type: 'field', name: 'thoi_gian_dk', label: 'Thời gian dự kiến thanh toán tạm ứng', input: 'date' },
        { type: 'spacer', kind: 'line', height: 8 },
        { type: 'section_title', text: 'Phần theo dõi công nợ tạm ứng của cá nhân:' },
        {
          type: 'field_row',
          fields: [
            { name: 'tong_chua_hoan', label: 'Tổng tạm ứng chưa hoàn', input: 'text' },
            { name: 'tri_gia_chua_hoan', label: 'Trị giá', input: 'number', suffix: 'đồng' },
          ],
        },
        {
          type: 'field_row',
          fields: [
            { name: 'so_qua_han', label: 'Số khoản tạm ứng quá hạn', input: 'text' },
            { name: 'tri_gia_qua_han', label: 'Trị giá', input: 'number', suffix: 'đồng' },
          ],
        },
        { type: 'field', name: 'ngay_lap', label: 'Ngày', input: 'date', autoFill: 'today' },
        {
          type: 'signatures',
          layout: 'row',
          items: [
            { label: 'Giám đốc điều hành' },
            { label: 'Kế toán trưởng' },
            { label: 'Trưởng khối' },
            { label: 'Phụ trách bộ phận' },
            { label: 'Người đề nghị', autoFillUser: true },
          ],
        },
      ],
    },
  },

  // ============================================================
  // 5. GIẤY BIÊN NHẬN TIỀN (TUTT/2025/06)
  // ============================================================
  {
    id: 'gbnt',
    name: 'Giấy biên nhận tiền',
    description: 'Biên nhận tiền giữa cá nhân và công ty. Có CMND, địa chỉ, lý do nhận, 2 chữ ký.',
    category: 'Tài chính',
    formCode: 'TUTT/2025/06',
    schema: {
      version: 1,
      paperSize: 'A4',
      formCode: 'TUTT/2025/06',
      blocks: [
        { type: 'header', showLogo: true, showCompany: true },
        { type: 'title', text: 'GIẤY BIÊN NHẬN TIỀN' },
        { type: 'field', name: 'ho_ten', label: 'Tên tôi là', input: 'text' },
        {
          type: 'field_row',
          fields: [
            { name: 'cmnd', label: 'Số CMND', input: 'text' },
            { name: 'cmnd_ngay', label: 'Ngày cấp', input: 'date' },
            { name: 'cmnd_noi', label: 'Nơi cấp', input: 'text' },
          ],
        },
        {
          type: 'field_row',
          fields: [
            { name: 'dia_chi', label: 'Địa chỉ', input: 'text' },
            { name: 'dien_thoai', label: 'Điện thoại', input: 'text' },
          ],
        },
        {
          type: 'field_row',
          fields: [
            { name: 'so_tien', label: 'Đã nhận', input: 'number', suffix: 'đồng' },
            { name: 'ngay_nhan', label: 'Vào ngày', input: 'date', autoFill: 'today' },
          ],
        },
        { type: 'field', name: 'bang_chu', label: 'Bằng chữ', input: 'text', readonly: true, computedWords: 'so_tien' },
        { type: 'field', name: 'dai_dien_cty', label: 'Từ Công ty CP Kinh Doanh Hưng Việt - Đại diện là anh/chị', input: 'text' },
        {
          type: 'field_row',
          fields: [
            { name: 'so_hd', label: 'Lý do: nhận tiền thanh toán của HĐ/PO số', input: 'text' },
            { name: 'ngay_hd', label: 'Ngày', input: 'date' },
            { name: 'lan', label: 'Lần', input: 'text' },
          ],
        },
        { type: 'field', name: 'noi_dung', label: 'Cho việc cung cấp dịch vụ/hàng hóa', input: 'textarea' },
        { type: 'text', text: 'Tôi xác nhận đã nhận đủ tiền và xin cam đoan thực hiện đầy đủ nghĩa vụ giao hàng/dịch vụ theo đúng thỏa thuận giữa hai bên.', italic: true },
        { type: 'field', name: 'ngay_lap', label: 'Ngày', input: 'date', autoFill: 'today' },
        {
          type: 'signatures',
          layout: 'row',
          items: [
            { label: 'Đại diện công ty giao tiền' },
            { label: 'Người nhận tiền' },
          ],
        },
      ],
    },
  },

  // ============================================================
  // 6. BIÊN BẢN KIỂM NHẬN THUỐC
  // ============================================================
  {
    id: 'bbkt',
    name: 'Biên bản kiểm nhận thuốc',
    description: 'Kiểm nhận thuốc nhập kho. Header 2 cột (Sở YT/CHXHCN VN), 3 đại diện (Khoa Dược/Kế toán/Bên giao), table 9 cột chi tiết thuốc.',
    category: 'Kho dược',
    schema: {
      version: 1,
      paperSize: 'A4',
      blocks: [
        {
          type: 'header_2col',
          left: { line1: 'SỞ Y TẾ HÀ NỘI', line2: 'CÔNG TY CP KINH DOANH HƯNG VIỆT' },
          right: { line1: 'CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM', line2: 'Độc lập – Tự do – Hạnh phúc' },
        },
        { type: 'spacer', kind: 'space', height: 16 },
        { type: 'title', text: 'BIÊN BẢN KIỂM NHẬN' },
        {
          type: 'field_row',
          fields: [
            { name: 'ngay', label: 'Hôm nay, ngày', input: 'date', autoFill: 'today' },
            { name: 'tai', label: 'Tại', input: 'text' },
          ],
        },
        { type: 'text', text: 'Ban kiểm nhận gồm:' },
        { type: 'field', name: 'rep_khoa_duoc', label: 'Khoa Dược', input: 'text', indent: true },
        { type: 'field', name: 'rep_ke_toan', label: 'Phòng kế toán', input: 'text', indent: true },
        { type: 'field', name: 'rep_ben_giao', label: 'Bên giao hàng', input: 'text', indent: true },
        {
          type: 'field_row',
          fields: [
            { name: 'so_hd', label: 'Cùng nhau kiểm nhận thuốc nhập theo hóa đơn số', input: 'text' },
            { name: 'ngay_hd', label: 'Ngày', input: 'date' },
            { name: 'cua_cty', label: 'Của công ty', input: 'text' },
          ],
        },
        {
          type: 'table',
          name: 'items',
          addable: true,
          minRows: 1,
          columns: [
            { name: '@index', label: 'TT', input: 'auto_index', width: 40 },
            { name: 'ten_thuoc', label: 'Tên thuốc, nồng độ, hàm lượng', input: 'text' },
            { name: 'dvt', label: 'ĐVT', input: 'text', width: 60 },
            { name: 'so_luong', label: 'Số lượng', input: 'number', width: 80 },
            { name: 'nuoc_sx', label: 'Nước sản xuất', input: 'text', width: 100 },
            { name: 'han_dung', label: 'Hạn dùng', input: 'date', width: 110 },
            { name: 'quy_cach', label: 'Quy cách', input: 'text', width: 90 },
            { name: 'cam_quan', label: 'Cảm quan', input: 'text', width: 90 },
            { name: 'lo_sx', label: 'Lô SX', input: 'text', width: 90 },
          ],
          totals: [{ name: 'tong_khoan', label: 'Tổng khoản', sumColumn: 'so_luong' }],
        },
        {
          type: 'signatures',
          layout: 'row',
          items: [
            { label: 'Khoa Dược' },
            { label: 'Phòng kế toán' },
            { label: 'Bên giao hàng' },
          ],
        },
      ],
    },
  },

  // ============================================================
  // 7. GIẤY BÁO HỎNG/HỦY HÀNG HÓA THIẾT BỊ
  // ============================================================
  {
    id: 'gbh',
    name: 'Giấy báo hỏng/hủy hàng hóa thiết bị',
    description: 'Báo cáo hàng hóa/thiết bị hỏng. Table 5 cột + 3 phương án xử lý (chuyển thanh lý/vứt/khác) + 4 chữ ký.',
    category: 'Kho',
    schema: {
      version: 1,
      paperSize: 'A4',
      blocks: [
        { type: 'text', text: 'Đơn vị: Công ty CP Kinh doanh Hưng Việt', bold: true },
        { type: 'field', name: 'bo_phan', label: 'Bộ phận', input: 'text', autoFill: 'current_user_dept' },
        { type: 'title', text: 'GIẤY BÁO HỎNG/HỦY HÀNG HÓA THIẾT BỊ' },
        { type: 'field', name: 'ngay', label: 'Ngày', input: 'date', autoFill: 'today' },
        {
          type: 'table',
          name: 'items',
          addable: true,
          minRows: 1,
          columns: [
            { name: '@index', label: 'STT', input: 'auto_index', width: 40 },
            { name: 'thong_tin', label: 'Thông tin hàng hóa/thiết bị hủy hỏng', input: 'text' },
            { name: 'so_luong', label: 'Số lượng', input: 'number', width: 90 },
            { name: 'ly_do', label: 'Lý do hỏng', input: 'text' },
            { name: 'ghi_chu', label: 'Ghi chú', input: 'text', width: 130 },
          ],
        },
        { type: 'section_title', text: 'Phương án đề xuất (bắt buộc chọn ít nhất một):' },
        {
          type: 'checkbox_row',
          items: [
            { name: 'pa_thanh_ly', label: 'Chuyển về phòng mua hàng thanh lý', exclusiveGroup: 'phuong_an' },
            { name: 'pa_vut', label: 'Vứt bỏ (hàng nhỏ gọn, không liên quan rác thải y tế)', exclusiveGroup: 'phuong_an' },
            { name: 'pa_khac', label: 'Khác', exclusiveGroup: 'phuong_an' },
          ],
        },
        { type: 'field', name: 'pa_khac_chi_tiet', label: 'Chi tiết phương án khác (nếu có)', input: 'textarea' },
        {
          type: 'signatures',
          layout: 'row',
          items: [
            { label: 'Xác nhận thủ kho' },
            { label: 'Xác nhận kỹ thuật' },
            { label: 'Xác nhận trưởng bộ phận' },
            { label: 'Người lập bảng', autoFillUser: true },
          ],
        },
      ],
    },
  },

  // ============================================================
  // 8. BIÊN BẢN GIAO NHẬN HÀNG HÓA NỘI BỘ
  // ============================================================
  {
    id: 'knnb',
    name: 'Biên bản giao nhận hàng hóa nội bộ',
    description: 'Giao nhận hàng hóa giữa các bộ phận trong cty. Header 2 cột + 2 bên (Bên A giao / Bên B nhận) với multi đại diện + table 7 cột.',
    category: 'Kho',
    schema: {
      version: 1,
      paperSize: 'A4',
      blocks: [
        {
          type: 'header_2col',
          left: { line1: 'CÔNG TY CỔ PHẦN KINH DOANH HƯNG VIỆT' },
          right: { line1: 'CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM', line2: 'Độc lập – Tự do – Hạnh phúc' },
        },
        { type: 'spacer', kind: 'space', height: 12 },
        { type: 'title', text: 'BIÊN BẢN GIAO NHẬN HÀNG HÓA NỘI BỘ' },
        { type: 'text', text: '(Áp dụng cho việc giao nhận hàng hóa nội bộ)', italic: true, align: 'center' },
        {
          type: 'field_row',
          fields: [
            { name: 'ngay', label: 'Ngày', input: 'date', autoFill: 'today' },
            { name: 'dia_chi', label: 'Tại địa chỉ', input: 'text' },
          ],
        },
        { type: 'text', text: 'Chúng tôi gồm:' },
        {
          type: 'party_section',
          title: '1. Bên A (Bên giao hàng):',
          name: 'party_a',
          defaultReps: 2,
          maxReps: 5,
        },
        {
          type: 'party_section',
          title: '2. Bộ phận nhận bàn giao (Bên nhận):',
          name: 'party_b',
          defaultReps: 2,
          maxReps: 5,
        },
        {
          type: 'field_row',
          fields: [
            { name: 'so_chung_tu', label: 'Theo chứng từ', input: 'text' },
            { name: 'ngay_chung_tu', label: 'Ngày', input: 'date' },
          ],
        },
        {
          type: 'table',
          name: 'items',
          addable: true,
          minRows: 1,
          columns: [
            { name: '@index', label: 'STT', input: 'auto_index', width: 40 },
            { name: 'ten_hang', label: 'Tên hàng hóa', input: 'text' },
            { name: 'dvt', label: 'ĐVT', input: 'text', width: 60 },
            { name: 'so_luong', label: 'Số lượng', input: 'number', width: 80 },
            { name: 'hang_sx', label: 'Hãng sản xuất', input: 'text', width: 110 },
            { name: 'han_su_dung', label: 'Hạn sử dụng', input: 'date', width: 110 },
            { name: 'tinh_trang', label: 'Tình trạng hàng', input: 'text', width: 110 },
          ],
        },
        { type: 'text', text: 'Ghi chú: Cột tên hàng hóa bao gồm các thông tin: tên hàng, model, số series, quy cách đóng gói, đặc tính, số lô sx…', italic: true },
        { type: 'text', text: 'Hai bên đồng ý giao nhận, thống nhất ký tên. Biên bản được lập thành 02 bản, mỗi bên giữ 01 bản có giá trị pháp lý như nhau.', italic: true },
        {
          type: 'signatures',
          layout: 'row',
          items: [
            { label: 'ĐẠI DIỆN BÊN NHẬN' },
            { label: 'ĐẠI DIỆN BÊN GIAO' },
          ],
        },
      ],
    },
  },

  // ============================================================
  // 9. BIÊN BẢN GIAO NHẬN HÀNG HÓA NHÀ CUNG CẤP
  // ============================================================
  {
    id: 'gnhh',
    name: 'Biên bản giao nhận hàng hóa nhà cung cấp',
    description: 'Tương tự giao nhận nội bộ nhưng có địa chỉ của 2 bên (NCC). Phù hợp khi nhập hàng từ NCC bên ngoài.',
    category: 'Kho',
    schema: {
      version: 1,
      paperSize: 'A4',
      blocks: [
        {
          type: 'header_2col',
          left: { line1: 'CÔNG TY CỔ PHẦN KINH DOANH HƯNG VIỆT' },
          right: { line1: 'CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM', line2: 'Độc lập – Tự do – Hạnh phúc' },
        },
        { type: 'spacer', kind: 'space', height: 12 },
        { type: 'title', text: 'BIÊN BẢN GIAO NHẬN HÀNG HÓA' },
        { type: 'text', text: '(Áp dụng cho việc giao nhận với nhà cung cấp)', italic: true, align: 'center' },
        {
          type: 'field_row',
          fields: [
            { name: 'ngay', label: 'Ngày', input: 'date', autoFill: 'today' },
            { name: 'dia_chi', label: 'Tại địa chỉ', input: 'text' },
          ],
        },
        { type: 'text', text: 'Chúng tôi gồm:' },
        {
          type: 'party_section',
          title: '1. Bên A (Bên giao hàng):',
          name: 'party_a',
          defaultReps: 2,
          maxReps: 5,
          showAddress: true,
        },
        {
          type: 'party_section',
          title: '2. Bộ phận nhận bàn giao (Bên nhận):',
          name: 'party_b',
          defaultReps: 2,
          maxReps: 5,
          showAddress: true,
        },
        {
          type: 'field_row',
          fields: [
            { name: 'so_chung_tu', label: 'Theo chứng từ', input: 'text' },
            { name: 'ngay_chung_tu', label: 'Ngày', input: 'date' },
          ],
        },
        {
          type: 'table',
          name: 'items',
          addable: true,
          minRows: 1,
          columns: [
            { name: '@index', label: 'STT', input: 'auto_index', width: 40 },
            { name: 'ten_hang', label: 'Tên hàng hóa', input: 'text' },
            { name: 'dvt', label: 'ĐVT', input: 'text', width: 60 },
            { name: 'so_luong', label: 'Số lượng', input: 'number', width: 80 },
            { name: 'hang_sx', label: 'Hãng sản xuất', input: 'text', width: 110 },
            { name: 'han_su_dung', label: 'Hạn sử dụng', input: 'date', width: 110 },
            { name: 'tinh_trang', label: 'Tình trạng hàng', input: 'text', width: 110 },
          ],
        },
        { type: 'text', text: 'Ghi chú: Cột tên hàng hóa bao gồm các thông tin: tên hàng, model, số series, quy cách đóng gói, đặc tính, số lô sx…', italic: true },
        { type: 'text', text: 'Hai bên đồng ý giao nhận, thống nhất ký tên. Biên bản được lập thành 02 bản, mỗi bên giữ 01 bản có giá trị pháp lý như nhau.', italic: true },
        {
          type: 'signatures',
          layout: 'row',
          items: [
            { label: 'ĐẠI DIỆN BÊN NHẬN' },
            { label: 'ĐẠI DIỆN BÊN GIAO' },
          ],
        },
      ],
    },
  },

  // ============================================================
  // 10. GIẤY ĐỀ NGHỊ THANH LÝ VTTH VÀ CCDC
  // ============================================================
  {
    id: 'dntl',
    name: 'Giấy đề nghị thanh lý VTTH và CCDC',
    description: 'Đề nghị thanh lý vật tư/CCDC hết hạn. Table 10 cột + 4 phương án NCC + 4 chữ ký dạng grid.',
    category: 'Kho',
    schema: {
      version: 1,
      paperSize: 'A4',
      blocks: [
        { type: 'text', text: 'Công Ty Cổ Phần Kinh Doanh Hưng Việt', bold: true },
        { type: 'text', text: 'Số nhà 34 Đại Cồ Việt, Phường Hai Bà Trưng, Thành Phố Hà Nội' },
        { type: 'field', name: 'so_phieu', label: 'Số', input: 'text' },
        { type: 'title', text: 'GIẤY ĐỀ NGHỊ THANH LÝ VTTH VÀ CCDC HẾT HẠN, KHÔNG SỬ DỤNG' },
        { type: 'text', text: 'Kính gửi: Ban Giám Đốc', bold: true },
        { type: 'field', name: 'nguoi_de_nghi', label: 'Người đề nghị', input: 'text', autoFill: 'current_user_name' },
        { type: 'field', name: 'bo_phan', label: 'Bộ phận', input: 'text', autoFill: 'current_user_dept' },
        { type: 'field', name: 'ngay_de_nghi', label: 'Ngày đề nghị', input: 'date', autoFill: 'today' },
        { type: 'text', text: 'Thủ kho và Phòng Mua hàng xin phê duyệt Ban Giám Đốc hủy/bán thanh lý danh mục các tài sản hỏng/hủy/không sử dụng tại các bộ phận, chi tiết như sau:', italic: true },
        {
          type: 'table',
          name: 'items',
          addable: true,
          minRows: 1,
          columns: [
            { name: '@index', label: 'STT', input: 'auto_index', width: 40 },
            { name: 'bo_phan_tai_san', label: 'Bộ phận', input: 'text', width: 110 },
            { name: 'ma_hang', label: 'Mã hàng', input: 'text', width: 90 },
            { name: 'ten_hang', label: 'Tên hàng', input: 'text' },
            { name: 'so_luong', label: 'SL', input: 'number', width: 60 },
            { name: 'nguyen_gia', label: 'Nguyên giá', input: 'number', width: 110 },
            { name: 'tong_gia_tri', label: 'Tổng giá trị', input: 'number', width: 110, computed: 'so_luong * nguyen_gia' },
            { name: 'mua_nam', label: 'Mua năm', input: 'text', width: 80 },
            { name: 'tinh_trang', label: 'Tình trạng', input: 'text', width: 100 },
            { name: 'phan_loai', label: 'Phân loại', input: 'text', width: 100 },
          ],
          totals: [{ name: 'tong_cong', label: 'Tổng cộng', sumColumn: 'tong_gia_tri' }],
        },
        { type: 'section_title', text: 'Phương án đề xuất thanh lý (bắt buộc chọn ít nhất một):' },
        {
          type: 'field_row',
          fields: [
            { name: 'ncc1', label: 'Nhà cung cấp 1', input: 'text' },
            { name: 'ncc1_dg', label: 'Đơn giá', input: 'number' },
            { name: 'ncc1_tien', label: 'Đề nghị thanh lý số tiền', input: 'number' },
          ],
        },
        {
          type: 'field_row',
          fields: [
            { name: 'ncc2', label: 'Nhà cung cấp 2', input: 'text' },
            { name: 'ncc2_dg', label: 'Đơn giá', input: 'number' },
            { name: 'ncc2_tien', label: 'Đề nghị thanh lý số tiền', input: 'number' },
          ],
        },
        {
          type: 'field_row',
          fields: [
            { name: 'ncc3', label: 'Nhà cung cấp 3', input: 'text' },
            { name: 'ncc3_dg', label: 'Đơn giá', input: 'number' },
            { name: 'ncc3_tien', label: 'Đề nghị thanh lý số tiền', input: 'number' },
          ],
        },
        {
          type: 'field_row',
          fields: [
            { name: 'khac', label: 'Khác', input: 'text' },
            { name: 'khac_tien', label: 'Đề nghị thanh lý số tiền', input: 'number' },
          ],
        },
        {
          type: 'signatures',
          layout: 'grid',
          items: [
            { label: 'Phòng Mua hàng' },
            { label: 'Thủ kho' },
            { label: 'Kế toán trưởng' },
            { label: 'Giám đốc điều hành' },
          ],
        },
      ],
    },
  },

  // ============================================================
  // 11. YÊU CẦU TẠO MÃ DỊCH VỤ MỚI (BP Giá)
  // ============================================================
  {
    id: 'gia2',
    name: 'Yêu cầu tạo mã dịch vụ mới',
    description: 'Áp dụng cho BP Giá khi cần thêm dịch vụ mới vào hệ thống. Info grid 2 ô + table 11 cột chi tiết DV + 4 chữ ký grid.',
    category: 'Dịch vụ',
    schema: {
      version: 1,
      paperSize: 'A4',
      blocks: [
        { type: 'header', showLogo: true, showCompany: true },
        { type: 'field', name: 'so_phieu', label: 'Số', input: 'text' },
        { type: 'title', text: 'YÊU CẦU TẠO MÃ DỊCH VỤ MỚI' },
        { type: 'field', name: 'ngay_de_nghi', label: 'Ngày đề nghị', input: 'date', autoFill: 'today' },
        {
          type: 'info_grid',
          columns: 2,
          cells: [
            { label: 'Bộ phận yêu cầu', name: 'dept_request', input: 'text', autoFill: 'current_user_dept' },
            { label: 'Bộ phận tiếp nhận', name: 'dept_receive', input: 'text' },
          ],
        },
        {
          type: 'table',
          name: 'items',
          addable: true,
          minRows: 1,
          columns: [
            { name: '@index', label: 'Số', input: 'auto_index', width: 40 },
            { name: 'ma', label: 'Mã', input: 'text', width: 80 },
            { name: 'ten_dv', label: 'Tên Dịch vụ', input: 'text' },
            { name: 'ten_bhyt', label: 'Tên BHYT', input: 'text' },
            { name: 'ma_bhyt', label: 'Mã BHYT', input: 'text', width: 80 },
            { name: 'gia_bhyt', label: 'Giá BHYT', input: 'number', width: 90 },
            { name: 'nhom_cp', label: 'Nhóm CP', input: 'text', width: 80 },
            { name: 'gia_nhap', label: 'Giá nhập (VND)', input: 'number', width: 100 },
            { name: 'gia_ban', label: 'Giá bán (VND)', input: 'number', width: 100 },
            { name: 'thang_so', label: 'Mức thặng số', input: 'text', width: 90 },
            { name: 'noi_thuc_hien', label: 'Nơi thực hiện', input: 'text' },
          ],
        },
        { type: 'field', name: 'yeu_cau', label: 'Yêu cầu', input: 'textarea' },
        { type: 'field', name: 'ngay_hieu_luc', label: 'Ngày hiệu lực', input: 'date' },
        { type: 'field', name: 'ly_do', label: 'Lý do yêu cầu', input: 'textarea' },
        {
          type: 'signatures',
          layout: 'grid',
          items: [
            { label: 'Người lập', autoFillUser: true },
            { label: 'Kiểm tra' },
            { label: 'Phê duyệt' },
            { label: 'Giám đốc' },
          ],
        },
      ],
    },
  },

  // ============================================================
  // 12. YÊU CẦU THAY ĐỔI TÊN/PHÍ DỊCH VỤ (BP Giá)
  // ============================================================
  {
    id: 'gia1',
    name: 'Yêu cầu thay đổi tên/phí dịch vụ',
    description: 'Đổi tên hoặc phí của DV đã có. Đặc biệt: table có HEADER 2 CẤP - cột "THAY ĐỔI TỪ" và "THAY ĐỔI THÀNH" mỗi nhóm có 7-8 cột con.',
    category: 'Dịch vụ',
    schema: {
      version: 1,
      paperSize: 'A4',
      blocks: [
        { type: 'header', showLogo: true, showCompany: true },
        { type: 'field', name: 'so_phieu', label: 'Số', input: 'text' },
        { type: 'title', text: 'YÊU CẦU THAY ĐỔI TÊN DỊCH VỤ/PHÍ DỊCH VỤ' },
        { type: 'field', name: 'ngay_de_nghi', label: 'Ngày đề nghị', input: 'date', autoFill: 'today' },
        {
          type: 'info_grid',
          columns: 2,
          cells: [
            { label: 'Bộ phận yêu cầu', name: 'dept_request', input: 'text', autoFill: 'current_user_dept' },
            { label: 'Bộ phận tiếp nhận', name: 'dept_receive', input: 'text' },
          ],
        },
        {
          type: 'table_grouped',
          name: 'items',
          addable: true,
          minRows: 1,
          groups: [
            { label: 'THAY ĐỔI TỪ', colspan: 9, background: '#fff7e6' },
            { label: 'THAY ĐỔI THÀNH', colspan: 7, background: '#e6f7ff' },
          ],
          columns: [
            { name: '@index', label: 'STT', input: 'auto_index', width: 40 },
            // 8 cột "TỪ"
            { name: 'tu_ma', label: 'Mã', input: 'text', width: 70 },
            { name: 'tu_nhom', label: 'Nhóm', input: 'text', width: 80 },
            { name: 'tu_ten', label: 'Tên', input: 'text' },
            { name: 'tu_ten_bhyt', label: 'Tên BHYT', input: 'text' },
            { name: 'tu_dv_tinh', label: 'ĐV tính', input: 'text', width: 70 },
            { name: 'tu_ma_bhyt', label: 'Mã BHYT', input: 'text', width: 80 },
            { name: 'tu_gia_bhyt', label: 'Giá BHYT', input: 'number', width: 90 },
            { name: 'tu_gia_mua', label: 'Giá mua', input: 'number', width: 90 },
            // 7 cột "THÀNH"
            { name: 'thanh_ten', label: 'Tên', input: 'text' },
            { name: 'thanh_ten_bhyt', label: 'Tên BHYT', input: 'text' },
            { name: 'thanh_dv_tinh', label: 'ĐV tính', input: 'text', width: 70 },
            { name: 'thanh_ma_bhyt', label: 'Mã BHYT', input: 'text', width: 80 },
            { name: 'thanh_gia_bhyt', label: 'Giá BHYT', input: 'number', width: 90 },
            { name: 'thanh_gia_mua', label: 'Giá mua', input: 'number', width: 90 },
            { name: 'thanh_gia_ban', label: 'Giá bán', input: 'number', width: 90 },
          ],
        },
        { type: 'field', name: 'yeu_cau', label: 'Yêu cầu', input: 'textarea' },
        { type: 'field', name: 'ngay_hieu_luc', label: 'Ngày hiệu lực', input: 'date' },
        { type: 'field', name: 'ly_do', label: 'Lý do yêu cầu', input: 'textarea' },
        {
          type: 'signatures',
          layout: 'grid',
          items: [
            { label: 'Người lập', autoFillUser: true },
            { label: 'Kiểm tra' },
            { label: 'Phê duyệt' },
            { label: 'Giám đốc' },
          ],
        },
      ],
    },
  },

  // ============================================================
  // 13. ĐỀ XUẤT HỢP TÁC BS/CTV MỚI (DXBSCTV) - V1.0.63i
  // ============================================================
  {
    id: 'dxbsctv',
    name: 'Đề xuất hợp tác BS/CTV mới',
    description: 'Mẫu đề xuất hợp tác Bác sĩ/Cộng tác viên mới (lưu hành nội bộ). Bảng thông tin BS/CTV (12 trường) + 3 vùng ký.',
    category: 'Hợp tác',
    formCode: 'DXBSCTV',
    schema: {
      version: 1,
      paperSize: 'A4',
      formCode: 'DXBSCTV',
      blocks: [
        { type: 'header', showLogo: true, showCompany: true },
        { type: 'title', text: 'ĐỀ XUẤT' },
        { type: 'text', text: 'V/v: Hợp tác với BS/CTV mới', bold: true, align: 'center' },
        { type: 'text', text: 'About: New Referrer', italic: true, align: 'center' },
        { type: 'text', text: 'Kính gửi: Ban Lãnh đạo Bệnh viện,', bold: true },
        {
          type: 'field',
          name: 'ngay_phe_duyet',
          label: 'Phòng kinh doanh đề xuất hợp tác với BS/CTV theo chính sách BLĐ phê duyệt ngày',
          input: 'date',
        },
        { type: 'text', text: 'Theo thông tin dưới đây:', italic: true },

        // ─── Thông tin BS/CTV - dạng các field xếp dọc ───
        { type: 'section_title', text: 'Thông tin BS/CTV' },
        { type: 'field', name: 'ho_va_ten', label: 'Họ và tên', input: 'text' },
        { type: 'field', name: 'dia_chi', label: 'Địa chỉ', input: 'textarea' },
        {
          type: 'field_row',
          fields: [
            { name: 'so_dien_thoai', label: 'Số điện thoại', input: 'text' },
            { name: 'ngay_sinh', label: 'Ngày/tháng/năm sinh', input: 'date' },
          ],
        },
        { type: 'field', name: 'nghe_nghiep_chuyen_khoa', label: 'Nghề nghiệp / Chuyên khoa', input: 'text' },
        { type: 'field', name: 'don_vi_cong_tac', label: 'Đơn vị công tác', input: 'text' },
        {
          type: 'field_row',
          fields: [
            { name: 'so_cmt', label: 'Số CMT/CCCD', input: 'text' },
            { name: 'noi_cap', label: 'Nơi cấp', input: 'text' },
          ],
        },
        { type: 'field', name: 'so_chung_chi_hanh_nghe', label: 'Số chứng chỉ hành nghề (nếu là BS)', input: 'text' },
        { type: 'section_title', text: 'Thông tin thanh toán' },
        { type: 'field', name: 'hinh_thuc_thanh_toan', label: 'Hình thức thanh toán', input: 'text' },
        { type: 'field', name: 'so_tai_khoan', label: 'Số tài khoản', input: 'text' },
        { type: 'field', name: 'ma_so_thue', label: 'Mã số thuế TNCN (BV kê khai và nộp thuế)', input: 'text' },
        { type: 'section_title', text: 'Thông tin hợp tác' },
        {
          type: 'field_row',
          fields: [
            { name: 'ngay_bat_dau_hop_tac', label: 'Ngày bắt đầu hợp tác', input: 'date' },
            { name: 'hinh_thuc_hop_tac', label: 'Hình thức hợp tác', input: 'text' },
          ],
        },
        { type: 'field', name: 'thong_tin_nguon', label: 'Thông tin nguồn', input: 'text' },
        // ─── Ngày đề xuất (auto today) ───
        {
          type: 'field',
          name: 'ngay_de_xuat',
          label: 'Hà Nội, ngày',
          input: 'date',
          autoFill: 'today',
          align: 'right',
          italic: true,
        },
        // ─── 3 vùng ký ───
        {
          type: 'signatures',
          layout: 'row',
          items: [
            { label: 'Người đề xuất', autoFillUser: true },
            { label: 'Trưởng bộ phận' },
            { label: 'Ban lãnh đạo phê duyệt' },
          ],
        },
      ],
    },
  },

  // ============================================================
  // 14. THỎA THUẬN HỢP TÁC CTV 2026 (HDCTV2026) - V1.0.63i
  // ============================================================
  {
    id: 'hdctv2026',
    name: 'Thỏa thuận hợp tác CTV 2026',
    description: 'Mẫu thỏa thuận hợp tác Cộng tác viên 2026. Văn bản pháp lý nhiều điều khoản, thông tin Bên A + Bên B, 2 vùng ký.',
    category: 'Hợp tác',
    formCode: 'HDCTV/2026',
    schema: {
      version: 1,
      paperSize: 'A4',
      formCode: 'HDCTV/2026',
      blocks: [
        // ─── Quốc hiệu ───
        { type: 'text', text: 'CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM', bold: true, align: 'center' },
        { type: 'text', text: 'Độc lập - Tự do - Hạnh phúc', bold: true, align: 'center' },
        { type: 'text', text: '─────────────────', align: 'center' },
        { type: 'title', text: 'THỎA THUẬN HỢP TÁC' },
        {
          type: 'field',
          name: 'so_hop_dong',
          label: 'Số',
          input: 'text',
          align: 'center',
          italic: true,
          suffix: '/HV/HDHT',
        },
        { type: 'text', text: '- Căn cứ Bộ Luật dân sự nước Cộng hoà xã hội chủ nghĩa Việt Nam;', italic: true },
        { type: 'text', text: '- Căn cứ vào nhu cầu và khả năng của mỗi bên;', italic: true },
        {
          type: 'field',
          name: 'ngay_ky_hop_dong',
          label: 'Hôm nay, ngày',
          input: 'date',
          autoFill: 'today',
        },
        { type: 'text', text: 'tại Văn phòng Công ty cổ phần kinh doanh Hưng Việt, Chúng tôi gồm có:' },

        // ─── BÊN A ───
        { type: 'section_title', text: '1. CÔNG TY CỔ PHẦN KINH DOANH HƯNG VIỆT (Bên A)' },
        { type: 'text', text: 'Trụ sở chính: Số nhà 34, phố Đại Cồ Việt, Phường Hai Bà Trưng, Thành phố Hà Nội, Việt Nam.' },
        {
          type: 'field_row',
          fields: [
            { name: 'ben_a_ma_so_thue', label: 'Mã số thuế', input: 'text' },
            { name: 'ben_a_nguoi_dai_dien', label: 'Người đại diện', input: 'text' },
          ],
        },
        { type: 'text', text: 'Chức vụ: Giám đốc điều hành' },
        { type: 'text', text: 'Và', bold: true, italic: true, align: 'center' },

        // ─── BÊN B ───
        { type: 'section_title', text: '2. Bên B (Cá nhân hợp tác)' },
        { type: 'field', name: 'ben_b_ho_ten', label: 'Ông (Bà)', input: 'text' },
        {
          type: 'field_row',
          fields: [
            { name: 'ben_b_cccd', label: 'Số CMT/CCCD', input: 'text' },
            { name: 'ben_b_dien_thoai', label: 'Điện thoại', input: 'text' },
          ],
        },
        { type: 'field', name: 'ben_b_dia_chi', label: 'Địa chỉ', input: 'textarea' },
        { type: 'field', name: 'ben_b_ma_so_thue', label: 'Mã số thuế', input: 'text' },
        {
          type: 'field_row',
          fields: [
            { name: 'ben_b_so_tai_khoan', label: 'Số tài khoản', input: 'text' },
            { name: 'ben_b_ngan_hang', label: 'Ngân hàng', input: 'text' },
          ],
        },
        { type: 'text', text: 'Sau khi bàn bạc chúng tôi đồng ý ký kết Thỏa thuận hợp tác với các điều khoản sau đây:', bold: true, italic: true },

        // ─── ĐIỀU 1 ───
        { type: 'section_title', text: 'Điều 1. Các quy định chung' },
        { type: 'text', text: '1.1. Luật và ngôn ngữ:', bold: true },
        { type: 'text', text: '- Thỏa thuận chịu sự điều tiết của hệ thống pháp luật của nước Cộng hoà Xã hội Chủ nghĩa Việt Nam và các văn bản hướng dẫn có liên quan.' },
        { type: 'text', text: '- Ngôn ngữ dùng trong Thỏa thuận là tiếng Việt Nam.' },
        { type: 'text', text: '- Đại diện: được hiểu là người đại diện cho 1 (một) đối tác hoặc 1 (một) nhóm các đối tác hợp tác với Bên A. Người đại diện chịu trách nhiệm kết nối, chi trả các chi phí liên quan đến hợp đồng này cho 1 cá nhân hoặc 1 nhóm các đối tác.' },
        { type: 'text', text: '1.2. Tuân thủ pháp luật:', bold: true },
        { type: 'text', text: '- Hai bên khi thực hiện Thỏa thuận, phải tuân thủ pháp luật hiện hành của nước Cộng hoà Xã hội Chủ nghĩa Việt Nam.' },
        { type: 'text', text: '- Bên B tạo điều kiện thuận lợi cho Bên A theo qui định của pháp luật.' },
        { type: 'text', text: '- Bên A phải thông báo, nộp tất cả các loại thuế, lệ phí, phí và có tất cả giấy phép và phê chuẩn, theo qui định của pháp luật liên quan.' },

        // ─── ĐIỀU 2 ───
        { type: 'section_title', text: 'Điều 2. Nội dung hợp tác' },
        { type: 'text', text: 'Bên A và Bên B nhất trí cùng nhau hợp tác trong các lĩnh vực:' },
        { type: 'text', text: '- Hợp tác và tư vấn chuyên môn;' },
        { type: 'text', text: '- Đào tạo;' },
        { type: 'text', text: '- Phát triển thị trường;' },

        // ─── ĐIỀU 3 ───
        { type: 'section_title', text: 'Điều 3. Thanh toán (nếu có)' },
        { type: 'text', text: '- Hình thức thanh toán: Tiền mặt/chuyển khoản;' },
        { type: 'text', text: '- Chi phí: theo cơ chế của Bên A;' },
        { type: 'text', text: '- Đồng tiền thanh toán: Việt Nam đồng.' },

        // ─── ĐIỀU 4 ───
        { type: 'section_title', text: 'Điều 4. Các nguyên tắc tài chính' },
        { type: 'text', text: 'Hai bên phải tuân thủ các nguyên tắc tài chính kế toán theo qui định của pháp luật.' },
        { type: 'text', text: 'Mọi khoản thu chi đều phải được ghi chép rõ ràng, đầy đủ, xác thực.' },

        // ─── ĐIỀU 5 ───
        { type: 'section_title', text: 'Điều 5. Quyền và nghĩa vụ của Bên A' },
        { type: 'text', text: 'Đảm bảo Bệnh viện/Phòng khám có đầy đủ các giấy phép theo quy định của pháp luật, quá trình hoạt động hành nghề của Bệnh viện/Phòng khám theo đúng các giấy phép được cấp và đúng quy định của pháp luật.' },
        { type: 'text', text: 'Bàn bạc cùng bên B giải quyết các vấn đề chung của Thỏa thuận.' },
        { type: 'text', text: 'Chịu trách nhiệm kê khai và nộp các khoản thuế, phí theo quy định của Nhà nước liên quan đến Thỏa thuận này.' },
        { type: 'text', text: 'Thông báo cho Bên B 03 tháng trước khi hết hạn Thỏa thuận hợp tác kinh doanh này nếu không tiếp tục tái ký Thỏa thuận.' },

        // ─── ĐIỀU 6 ───
        { type: 'section_title', text: 'Điều 6. Quyền và nghĩa vụ của Bên B' },
        { type: 'text', text: 'Thực hiện công việc đúng theo yêu cầu đã thỏa thuận.' },
        { type: 'text', text: 'Đảm bảo chất lượng công việc và chịu trách nhiệm về kết quả công việc đã thực hiện.' },
        { type: 'text', text: 'Tuân thủ các quy định về bảo mật theo yêu cầu của bên A.' },
        { type: 'text', text: 'Bàn bạc cùng bên A giải quyết các vấn đề chung của Thỏa thuận.' },
        { type: 'text', text: 'Thông báo cho Bên A 03 tháng trước khi hết hạn Thỏa thuận hợp tác này nếu không tiếp tục tái ký Thỏa thuận.' },

        // ─── ĐIỀU 7 ───
        { type: 'section_title', text: 'Điều 7. Điều khoản chung' },
        { type: 'text', text: 'Thỏa thuận này được hiểu và chịu sự điều chỉnh của Pháp luật nước Cộng hoà xã hội chủ nghĩa Việt Nam.' },
        { type: 'text', text: 'Hai bên cam kết thực hiện tất cả những điều khoản đã cam kết trong Thỏa thuận. Bên nào vi phạm Thỏa thuận gây thiệt hại cho bên kia (trừ trong trường hợp bất khả kháng) thì phải bồi thường toàn bộ thiệt hại xảy ra.' },
        { type: 'text', text: 'Trong quá trình thực hiện Thỏa thuận nếu bên nào có khó khăn trở ngại thì phải báo cho bên kia trong vòng 05 (năm) ngày kể từ ngày có khó khăn trở ngại.' },
        { type: 'text', text: 'Các bên có trách nhiệm thông tin kịp thời cho nhau tiến độ thực hiện công việc. Đảm bảo bí mật mọi thông tin liên quan tới quá trình hợp tác.' },
        { type: 'text', text: 'Mọi sửa đổi, bổ sung Thỏa thuận này đều phải được làm bằng văn bản và có chữ ký của hai bên. Các phụ lục là phần không tách rời của Thỏa thuận.' },
        { type: 'text', text: 'Mọi tranh chấp phát sinh trong quá trình thực hiện Thỏa thuận được giải quyết trước hết qua thương lượng, hoà giải, nếu hoà giải không thành việc tranh chấp sẽ được giải quyết tại Toà án có thẩm quyền.' },

        // ─── ĐIỀU 8 ───
        { type: 'section_title', text: 'Điều 8. Tính độc lập và bảo mật' },
        { type: 'text', text: 'Theo thỏa thuận hợp tác trong Thỏa thuận này, Bên B là cá nhân, đối tác độc lập với bên A. Bên B cần tuân thủ về các chuẩn mực nghề nghiệp về tính bảo mật và sử dụng thông tin liên quan đến thông tin của Bên A.' },
        { type: 'text', text: 'Bên B không có quyền đứng ra đại diện pháp luật, phát ngôn, cam kết nào thay mặt cho bên A, hoặc tiết lộ thông tin của bên A cho cá nhân hay một bên bất kỳ nào, trừ trường hợp được bên A đồng ý bằng văn bản.' },

        // ─── ĐIỀU 9 ───
        { type: 'section_title', text: 'Điều 9. Nghĩa vụ Thuế và phí' },
        { type: 'text', text: 'Bên A chịu trách nhiệm nộp các loại thuế và phí trong phạm vi Thỏa thuận này theo quy định của pháp luật hiện hành.' },
        { type: 'text', text: 'Bên A sẽ không chịu trách nhiệm trước bất kỳ khoản bồi hoàn nào liên quan nghĩa vụ Thuế Phí của bên B trong trường hợp có sự khiếu nại, hoặc xác minh từ các cơ quan ban ngành ngoài phạm vi Thỏa thuận này.' },

        // ─── ĐIỀU 10 ───
        { type: 'section_title', text: 'Điều 10. Thời gian hợp tác và Hiệu lực Thỏa thuận' },
        {
          type: 'field_row',
          fields: [
            { name: 'ngay_bat_dau', label: 'Từ ngày', input: 'date' },
            { name: 'ngay_ket_thuc', label: 'Đến ngày', input: 'date' },
          ],
        },
        { type: 'text', text: 'Khi kết thúc Thỏa thuận, hai bên sẽ làm biên bản thanh lý Thỏa thuận.' },
        { type: 'text', text: 'Thỏa thuận này gồm ba (03) trang không thể tách rời nhau, được lập thành 02 (hai) bản, mỗi Bên giữ 01 (một) bản có giá trị pháp lý như nhau.' },

        // ─── 2 vùng ký ───
        {
          type: 'signatures',
          layout: 'row',
          items: [
            { label: 'Đại diện Bên A' },
            { label: 'Đại diện Bên B' },
          ],
        },
      ],
    },
  },
];
